import { debugError, debugInfo, debugWarn } from '../utils/debugLog';
import { ErrorType, ErrorSeverity, ErrorContext, ErrorEntry } from '../contexts/GlobalErrorContext';
import { Sentry, isSentryReady } from '../sentryClient';

const delay = (ms: number) =>
  new Promise<void>(resolve => {
    const timeoutId = setTimeout(() => {
      clearTimeout(timeoutId);
      resolve();
    }, ms);
  });

// Error reporting configuration
export interface ErrorReportingConfig {
  enableConsoleLogging: boolean;
  enableAnalytics: boolean;
  enableRemoteReporting: boolean;
  maxRetries: number;
  retryDelay: number;
  batchSize: number;
  flushInterval: number;
}

// Default configuration
const defaultConfig: ErrorReportingConfig = {
  enableConsoleLogging: process.env.NODE_ENV === 'development',
  enableAnalytics: true,
  enableRemoteReporting: process.env.NODE_ENV === 'production' || isSentryReady(),
  maxRetries: 3,
  retryDelay: 1000,
  batchSize: 10,
  flushInterval: 30000, // 30 seconds
};

// Error report interface for external services
export interface ErrorReport {
  id: string;
  type: ErrorType;
  severity: ErrorSeverity;
  context: ErrorContext;
  message: string;
  stack?: string;
  timestamp: string;
  userAgent: string;
  url: string;
  userId?: string;
  sessionId: string;
  metadata?: Record<string, any>;
  retryCount: number;
  originalError?: Error;
}

// Analytics data interface
export interface ErrorAnalyticsData {
  errorCount: number;
  errorRate: number;
  topErrors: Array<{ type: ErrorType; count: number }>;
  contextBreakdown: Record<ErrorContext, number>;
  severityBreakdown: Record<ErrorSeverity, number>;
  timeRange: {
    start: string;
    end: string;
  };
}

// Error pattern detection
export interface ErrorPattern {
  type: ErrorType;
  context: ErrorContext;
  frequency: number;
  lastOccurrence: Date;
  isRecurring: boolean;
  suggestedAction?: string;
}

class GlobalErrorReportingService {
  private config: ErrorReportingConfig;
  private errorQueue: ErrorReport[] = [];
  private sessionId: string;
  private flushTimer: NodeJS.Timeout | null = null;
  private errorPatterns: Map<string, ErrorPattern> = new Map();

  constructor(config: Partial<ErrorReportingConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
    this.sessionId = this.generateSessionId();
    this.startFlushTimer();
  }

  // Generate unique session ID
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Start automatic flush timer
  private startFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = setInterval(() => {
      this.flushErrorQueue();
    }, this.config.flushInterval);
  }

  // Convert ErrorEntry to ErrorReport
  private createErrorReport(error: ErrorEntry): ErrorReport {
    return {
      id: error.id,
      type: error.type,
      severity: error.severity,
      context: error.context,
      message: error.message,
      stack: error.originalError?.stack,
      timestamp: error.timestamp.toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
      sessionId: this.sessionId,
      metadata: error.metadata,
      retryCount: error.retryCount,
      originalError: error.originalError,
    };
  }

  // Report error to the service
  public reportError(error: ErrorEntry): void {
    const errorReport = this.createErrorReport(error);

    // Console logging
    if (this.config.enableConsoleLogging) {
      this.logToConsole(errorReport);
    }

    // Add to queue for batch processing
    if (this.config.enableRemoteReporting || this.config.enableAnalytics) {
      this.errorQueue.push(errorReport);
    }

    // Update error patterns
    this.updateErrorPatterns(error);

    // Forward to Sentry for centralized telemetry
    this.captureWithSentry(errorReport);

    // Flush immediately for critical errors
    if (error.severity === 'critical') {
      this.flushErrorQueue();
    }

    // Flush if queue is full
    if (this.errorQueue.length >= this.config.batchSize) {
      this.flushErrorQueue();
    }
  }

  // Console logging with formatting
  private logToConsole(errorReport: ErrorReport): void {
    const prefix = `[GlobalError:${errorReport.severity.toUpperCase()}]`;
    const context = `${errorReport.context}:${errorReport.type}`;
    
    switch (errorReport.severity) {
      case 'critical':
        debugError(`${prefix} ${context}`, errorReport.message, errorReport);
        break;
      case 'high':
        debugError(`${prefix} ${context}`, errorReport.message, errorReport);
        break;
      case 'medium':
        debugWarn(`${prefix} ${context}`, errorReport.message, errorReport);
        break;
      case 'low':
        debugInfo(`${prefix} ${context}`, errorReport.message, errorReport);
        break;
    }
  }

  // Update error patterns for detection
  private updateErrorPatterns(error: ErrorEntry): void {
    const patternKey = `${error.context}:${error.type}`;
    const existing = this.errorPatterns.get(patternKey);

    if (existing) {
      existing.frequency++;
      existing.lastOccurrence = error.timestamp;
      existing.isRecurring = existing.frequency > 2;
    } else {
      this.errorPatterns.set(patternKey, {
        type: error.type,
        context: error.context,
        frequency: 1,
        lastOccurrence: error.timestamp,
        isRecurring: false,
      });
    }

    // Add suggested actions for common patterns
    this.addSuggestedActions(patternKey);
  }

  // Add suggested actions for error patterns
  private addSuggestedActions(patternKey: string): void {
    const pattern = this.errorPatterns.get(patternKey);
    if (!pattern) return;

    // Define suggested actions based on error patterns
    const suggestions: Record<string, string> = {
      'schedule:network': 'Check internet connection and retry',
      'shows:network': 'Verify API endpoint availability',
      'audio:network': 'Check audio streaming service status',
      'store:network': 'Verify Shopify API connection',
      'schedule:timeout': 'Increase request timeout or check server performance',
      'shows:timeout': 'Optimize query or check database performance',
      'audio:timeout': 'Check audio file availability and CDN performance',
      'store:validation': 'Verify product data format and required fields',
      'cart:validation': 'Check cart item data structure',
      'checkout:validation': 'Verify checkout form data and payment information',
    };

    pattern.suggestedAction = suggestions[patternKey] || 'Review error details and check system status';
  }

  private captureWithSentry(errorReport: ErrorReport): void {
    if (!isSentryReady() || typeof Sentry?.withScope !== 'function') {
      return;
    }

    const level = this.mapSeverityToSentryLevel(errorReport.severity);

    try {
      if (typeof Sentry.addBreadcrumb === 'function') {
        Sentry.addBreadcrumb({
          category: 'global-error',
          message: errorReport.message,
          level,
          data: {
            context: errorReport.context,
            type: errorReport.type,
            severity: errorReport.severity,
            retryCount: errorReport.retryCount,
            sessionId: errorReport.sessionId,
            metadata: errorReport.metadata,
          },
        });
      }

      Sentry.withScope(scope => {
        scope.setTag?.('error.context', errorReport.context);
        scope.setTag?.('error.type', errorReport.type);
        scope.setTag?.('error.severity', errorReport.severity);
        scope.setTag?.('error.sessionId', errorReport.sessionId);
        scope.setLevel?.(level as any);
        scope.setFingerprint?.([
          errorReport.context,
          errorReport.type,
          errorReport.message.slice(0, 120),
        ]);

        scope.setExtra?.('error.url', errorReport.url);
        scope.setExtra?.('error.userAgent', errorReport.userAgent);
        scope.setExtra?.('error.retryCount', errorReport.retryCount);
        scope.setExtra?.('error.timestamp', errorReport.timestamp);
        if (errorReport.metadata) {
          scope.setExtra?.('error.metadata', errorReport.metadata);
        }

        const errorToCapture = errorReport.originalError ?? new Error(errorReport.message);
        if (!errorReport.originalError) {
          errorToCapture.name = `Captured:${errorReport.context}:${errorReport.type}`;
        }

        if (Sentry.captureException) {
          Sentry.captureException(errorToCapture);
        } else if (Sentry.captureMessage) {
          Sentry.captureMessage(errorReport.message, level);
        }
      });
    } catch (sentryError) {
      debugWarn('[GlobalErrorReporting] Failed to forward error to Sentry', sentryError);
    }
  }

  private mapSeverityToSentryLevel(severity: ErrorSeverity): 'fatal' | 'error' | 'warning' | 'info' {
    switch (severity) {
      case 'critical':
        return 'fatal';
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
      default:
        return 'info';
    }
  }

  // Flush error queue to external services
  private async flushErrorQueue(): Promise<void> {
    if (this.errorQueue.length === 0) return;

    const errorsToFlush = [...this.errorQueue];
    this.errorQueue = [];

    try {
      // Send to analytics service
      if (this.config.enableAnalytics) {
        await this.sendToAnalytics(errorsToFlush);
      }

      // Send to remote error reporting service
      if (this.config.enableRemoteReporting) {
        await this.sendToRemoteService(errorsToFlush);
      }
    } catch (error) {
      // If flushing fails, add errors back to queue for retry
      this.errorQueue.unshift(...errorsToFlush);
      
      if (this.config.enableConsoleLogging) {
        debugError('[GlobalErrorReporting] Failed to flush error queue:', error);
      }
    }
  }

  // Send errors to analytics service
  private async sendToAnalytics(errors: ErrorReport[]): Promise<void> {
    // In a real implementation, this would send to your analytics service
    // For now, we'll just log the analytics data
    const analyticsData = this.generateAnalyticsData(errors);
    
    if (this.config.enableConsoleLogging) {
      debugInfo('[GlobalErrorReporting] Analytics data:', analyticsData);
    }

    // Simulate API call
    return delay(100);
  }

  // Send errors to remote error reporting service
  private async sendToRemoteService(errors: ErrorReport[]): Promise<void> {
    // In a real implementation, this would send to services like Sentry, Bugsnag, etc.
    // For now, we'll simulate the API call
    
    if (this.config.enableConsoleLogging) {
      debugInfo('[GlobalErrorReporting] Sending to remote service:', errors.length, 'errors');
    }

    // Simulate API call with retry logic
    let retries = 0;
    while (retries < this.config.maxRetries) {
      try {
        const attemptNumber = retries;
        // Simulate API call
        await new Promise((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            clearTimeout(timeoutId);
            // Simulate occasional failures for testing
            if (Math.random() > 0.9 && attemptNumber < 2) {
              reject(new Error('Simulated API failure'));
            } else {
              resolve(undefined);
            }
          }, 100);
        });
        
        break; // Success, exit retry loop
      } catch (error) {
        retries++;
        if (retries >= this.config.maxRetries) {
          throw error;
        }
        
        // Wait before retry
        await delay(this.config.retryDelay * retries);
      }
    }
  }

  // Generate analytics data from errors
  private generateAnalyticsData(errors: ErrorReport[]): ErrorAnalyticsData {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

    // Count errors by type
    const errorCounts: Record<ErrorType, number> = {
      network: 0,
      validation: 0,
      notFound: 0,
      server: 0,
      permission: 0,
      timeout: 0,
      generic: 0,
    };

    const contextCounts: Record<ErrorContext, number> = {
      schedule: 0,
      shows: 0,
      audio: 0,
      store: 0,
      cart: 0,
      checkout: 0,
      global: 0,
    };

    const severityCounts: Record<ErrorSeverity, number> = {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0,
    };

    errors.forEach(error => {
      errorCounts[error.type]++;
      contextCounts[error.context]++;
      severityCounts[error.severity]++;
    });

    // Get top errors
    const topErrors = Object.entries(errorCounts)
      .map(([type, count]) => ({ type: type as ErrorType, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      errorCount: errors.length,
      errorRate: errors.length / 60, // errors per minute (assuming 1 hour window)
      topErrors,
      contextBreakdown: contextCounts,
      severityBreakdown: severityCounts,
      timeRange: {
        start: oneHourAgo.toISOString(),
        end: now.toISOString(),
      },
    };
  }

  // Get error patterns
  public getErrorPatterns(): ErrorPattern[] {
    return Array.from(this.errorPatterns.values())
      .sort((a, b) => b.frequency - a.frequency);
  }

  // Get recurring errors
  public getRecurringErrors(): ErrorPattern[] {
    return this.getErrorPatterns().filter(pattern => pattern.isRecurring);
  }

  // Clear error patterns
  public clearErrorPatterns(): void {
    this.errorPatterns.clear();
  }

  // Update configuration
  public updateConfig(newConfig: Partial<ErrorReportingConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Restart flush timer if interval changed
    if (newConfig.flushInterval !== undefined) {
      this.startFlushTimer();
    }
  }

  // Get current configuration
  public getConfig(): ErrorReportingConfig {
    return { ...this.config };
  }

  // Manual flush
  public async flush(): Promise<void> {
    await this.flushErrorQueue();
  }

  // Cleanup
  public destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    
    // Flush remaining errors
    this.flushErrorQueue();
  }
}

// Create singleton instance
export const globalErrorReportingService = new GlobalErrorReportingService();

// Export service class for testing
export { GlobalErrorReportingService };

export default globalErrorReportingService;
